/**
 * Facility-tagged appointment categories → one canonical category name.
 *
 * Every facility carries its own copy of an appointment type, and each copy
 * sits under a category named for that facility: "CC Vitamin Shots",
 * "NY-MA Vitamin Shots", "NB Vitamin Shots" — and sometimes a bare
 * "Vitamin Shots" with no tag at all. The Type filter already collapses the
 * types themselves by name (useAppointmentFilterOptions); this collapses their
 * categories the same way so those collapsed types can group under one header.
 *
 * Nothing here is persisted, and no facility-tag list is configured anywhere.
 * The canonical names are display-only — the filter still stores raw
 * appointmentTypeIDs — so re-clustering after a category is renamed can never
 * invalidate a saved dynamic list.
 *
 * The clustering deliberately errs toward NOT merging. A wrong merge is
 * invisible to the user (two unrelated services quietly filed under one
 * header); an un-merged category is merely the status quo. So a leading token
 * is only treated as a facility tag when the data corroborates it — see
 * `canonicalizeCategories` below. Anything left unclustered keeps its raw name
 * as its own header.
 */

export interface CategoryGroup {
  groupId: number;
  groupName: string;
  /**
   * Legacy `appointment_type_group.groupFacilityID`. `0` means "global"
   * (visible in every facility); any other value scopes the group to one
   * facility. Treated here as an opaque scope id, `0` included.
   */
  groupFacilityId: number;
  /**
   * Names of the appointment types filed under this group. The strongest
   * available evidence that two categories are the same category: if each
   * facility's copy holds an identically-named type, they are copies of one
   * another. Optional — clustering falls back to name-shape evidence when it
   * is not supplied.
   */
  typeNames?: string[];
}

/**
 * Longest facility tag we will strip. One token covers "CC" and "NY-MA"
 * (hyphenated, so still one token); two covers "New Brunswick Vitamin Shots".
 * Beyond that the odds of eating a real category name outweigh the benefit.
 */
const MAX_PREFIX_TOKENS = 2;

/** A suffix shorter than this is too generic to cluster on. */
const MIN_SUFFIX_CHARS = 3;

const squashWhitespace = (value: string) => value.trim().replace(/\s+/g, ' ');

interface Row {
  groupId: number;
  facilityId: number;
  /** Whitespace-normalized original, preserving case. */
  name: string;
  tokens: string[];
  /** Lowercased type names filed under this group. */
  typeNames: Set<string>;
}

interface Candidate {
  row: Row;
  /** How many leading tokens were stripped to reach this suffix. */
  prefixLen: number;
  /** The suffix in its original casing. */
  suffix: string;
}

const scopedPrefixKey = (facilityId: number, prefix: string) =>
  `${facilityId} ${prefix.toLowerCase()}`;

/**
 * Map every group id to the category name it should display under.
 *
 * Two groups cluster only when all of the following hold:
 *
 *  1. Their names share a trailing phrase (the candidate category).
 *  2. They belong to different facility scopes. Same-facility neighbours like
 *     "Follow Up Consults" / "New Consults" are two real categories at one
 *     site, not one category tagged twice, and `groupFacilityID` says so
 *     outright — no string heuristic needed.
 *  3. The stripped prefix is corroborated as a facility tag, by any of:
 *     (a) the two groups holding an identically-named appointment type. Each
 *         facility keeps its own copy of a type under its own copy of the
 *         category, so a shared type name is near-proof the categories are
 *         copies too. This is the common case and the strongest signal — it is
 *         also exactly what the Type filter already merges on.
 *     (b) an untagged sibling existing — a bare "Vitamin Shots" group proves
 *         that is the real category name; or
 *     (c) that same prefix leading two or more distinct groups within the same
 *         facility — "NB" fronting NB Vitamin Shots, NB Botox and NB Consults
 *         is a tag; a lone "Follow Up" is part of a name.
 *
 * Groups matching nothing keep their raw name.
 *
 * Note what (a) buys: "Follow Up Consults" and "New Consults" at two different
 * facilities are indistinguishable from "CC Vitamin Shots" / "NB Vitamin
 * Shots" by name shape alone. Only their contents tell them apart.
 */
export function canonicalizeCategories(groups: CategoryGroup[]): Map<number, string> {
  const rows: Row[] = [];
  for (const group of groups) {
    const name = squashWhitespace(group.groupName ?? '');
    if (!name) continue;
    rows.push({
      groupId: group.groupId,
      facilityId: group.groupFacilityId,
      name,
      tokens: name.split(' '),
      typeNames: new Set(
        (group.typeNames ?? []).map((t) => squashWhitespace(t).toLowerCase()).filter(Boolean),
      ),
    });
  }

  // Rule 3b's evidence: which groups does each (facility, leading-prefix) pair
  // front? Built across every prefix length so "NB" and "New Brunswick" are
  // each counted on their own terms.
  const groupsByScopedPrefix = new Map<string, Set<number>>();
  for (const row of rows) {
    const maxPrefix = Math.min(MAX_PREFIX_TOKENS, row.tokens.length - 1);
    for (let prefixLen = 1; prefixLen <= maxPrefix; prefixLen++) {
      const key = scopedPrefixKey(row.facilityId, row.tokens.slice(0, prefixLen).join(' '));
      const seen = groupsByScopedPrefix.get(key) ?? new Set<number>();
      seen.add(row.groupId);
      groupsByScopedPrefix.set(key, seen);
    }
  }

  // Candidate clusters, keyed by lowercased trailing phrase. A row appears once
  // per prefix length, so it can be a member of several competing clusters.
  const buckets = new Map<string, Candidate[]>();
  for (const row of rows) {
    const maxPrefix = Math.min(MAX_PREFIX_TOKENS, row.tokens.length - 1);
    for (let prefixLen = 0; prefixLen <= maxPrefix; prefixLen++) {
      const suffix = row.tokens.slice(prefixLen).join(' ');
      if (suffix.length < MIN_SUFFIX_CHARS) continue;
      const key = suffix.toLowerCase();
      const bucket = buckets.get(key) ?? [];
      bucket.push({ row, prefixLen, suffix });
      buckets.set(key, bucket);
    }
  }

  /** Cluster key → the display spelling its members should share. */
  const displayNameByKey = new Map<string, string>();

  for (const [key, members] of buckets) {
    const hasUntagged = members.some((m) => m.prefixLen === 0);
    const qualified = members.filter((m) => {
      if (m.prefixLen === 0) return true;
      if (hasUntagged) return true; // rule 3b
      // rule 3a — shares a type name with another facility's copy
      const sharesType = members.some(
        (other) =>
          other.row.groupId !== m.row.groupId &&
          other.row.facilityId !== m.row.facilityId &&
          [...m.row.typeNames].some((name) => other.row.typeNames.has(name)),
      );
      if (sharesType) return true;
      const prefix = m.row.tokens.slice(0, m.prefixLen).join(' ');
      const fronted = groupsByScopedPrefix.get(scopedPrefixKey(m.row.facilityId, prefix));
      return (fronted?.size ?? 0) >= 2; // rule 3c
    });

    if (qualified.length < 2) continue; // rule 1
    const scopes = new Set(qualified.map((m) => m.row.facilityId));
    if (scopes.size < 2) continue; // rule 2

    // Prefer the untagged group's own spelling — that is the account's
    // preferred wording. Otherwise take a stable pick so the header text does
    // not shuffle between loads.
    const untagged = qualified.find((m) => m.prefixLen === 0);
    const chosen =
      untagged ??
      [...qualified].sort(
        (a, b) => a.suffix.localeCompare(b.suffix) || a.row.groupId - b.row.groupId,
      )[0];
    displayNameByKey.set(key, chosen.suffix);
  }

  const canonical = new Map<number, string>();
  for (const row of rows) {
    // Shortest strip wins: the longest surviving suffix is the most specific
    // cluster this group belongs to, so "CC Vitamin Shots" lands on
    // "Vitamin Shots" rather than a broader "Shots".
    let best: string | undefined;
    const maxPrefix = Math.min(MAX_PREFIX_TOKENS, row.tokens.length - 1);
    for (let prefixLen = 0; prefixLen <= maxPrefix; prefixLen++) {
      const key = row.tokens.slice(prefixLen).join(' ').toLowerCase();
      const display = displayNameByKey.get(key);
      if (display !== undefined) {
        best = display;
        break;
      }
    }
    canonical.set(row.groupId, best ?? row.name);
  }
  return canonical;
}

/**
 * Pick the category a merged type belongs under, by majority of its members.
 *
 * A type name collapsed across facilities can land in more than one canonical
 * category (a "Consultation" under both Vitamin Shots and Botox). The filter
 * shows one checkbox per name and `FilterOption.group` holds one string, so
 * one has to win — and by product decision that is the most common one.
 *
 * Ties go to whichever appeared first, which is the caller's iteration order
 * (`atOrder` as returned by the API), keeping the choice stable across loads.
 */
export function majorityCategory(categories: string[]): string | undefined {
  const counts = new Map<string, number>();
  for (const category of categories) {
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  let winner: string | undefined;
  let winningCount = 0;
  for (const [category, count] of counts) {
    if (count > winningCount) {
      winner = category;
      winningCount = count;
    }
  }
  return winner;
}
